/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { usePageRuntimeStore } from '@coze-studio/bot-detail-store/page-runtime';
import {
  type ModelInfoConfig,
  useModelStore,
} from '@coze-studio/bot-detail-store/model';
import { useBotInfoStore } from '@coze-studio/bot-detail-store/bot-info';
import { initBotDetailStore } from '@coze-studio/bot-detail-store';
import { I18n } from '@coze-arch/i18n';
import { BotPageFromEnum } from '@coze-arch/bot-typings/common';
import { type Model } from '@coze-arch/bot-api/developer_api';

type ModelInfoConfigWithName = ModelInfoConfig & { model_name?: string };

function isOntologyModelSource(modelSource: string | undefined): boolean {
  return modelSource === 'ontology';
}

function resolveLlmLabel(config: ModelInfoConfig, modelList: Model[]): string {
  const modelName =
    (config as ModelInfoConfigWithName).model_name?.trim() ?? '';
  if (modelName) {
    return modelName;
  }
  const matched = modelList.find(item => item.model_type === config.model);
  if (matched?.name?.trim()) {
    return matched.name.trim();
  }
  return config.model?.trim() || I18n.t('abr_app_info_unconfigured');
}

function resolveOntologyLabel(config: ModelInfoConfig): string {
  if (!isOntologyModelSource(config.model_source)) {
    return I18n.t('abr_app_info_not_used');
  }
  const name = config.ontology_app_name?.trim();
  const id = config.ontology_app_id?.trim();
  if (name) {
    return name;
  }
  if (id) {
    return id;
  }
  return I18n.t('abr_app_info_unconfigured');
}

export function usePublishAppMetadata(botId?: string, spaceId?: string) {
  const [loading, setLoading] = useState(true);

  const { name, description, iconUrl } = useBotInfoStore(
    useShallow(state => ({
      name: state.name,
      description: state.description,
      iconUrl: state.icon_url,
    })),
  );

  const { config, modelList } = useModelStore(
    useShallow(state => ({
      config: state.config as ModelInfoConfig,
      modelList: state.modelList,
    })),
  );

  useEffect(() => {
    if (!botId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const botState = useBotInfoStore.getState();
        const modelConfig = useModelStore.getState().config as ModelInfoConfig;
        const hasCachedMeta =
          botState.botId === botId &&
          Boolean(botState.name?.trim()) &&
          (Boolean(modelConfig.model) ||
            isOntologyModelSource(modelConfig.model_source));

        if (!hasCachedMeta) {
          useBotInfoStore.getState().setBotInfo({
            botId,
            space_id: spaceId ?? '',
          });
          usePageRuntimeStore.getState().setPageRuntimeBotInfo({
            pageFrom: BotPageFromEnum.Bot,
          });
          await initBotDetailStore();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [botId, spaceId]);

  const llmLabel = useMemo(
    () => resolveLlmLabel(config, modelList),
    [config, modelList],
  );
  const ontologyLabel = useMemo(() => resolveOntologyLabel(config), [config]);

  return {
    loading,
    name,
    description,
    iconUrl,
    llmLabel,
    ontologyLabel,
  };
}
